/**
 * API client for distribution_transactions (SQL).
 * Base URL from getBaseUrl() (REACT_APP_DISTRIBUTION_API_URL_DEV / REACT_APP_DISTRIBUTION_API_URL_PROD).
 * Expects backend to expose:
 *   GET /distribution-transactions?distribution_project_id=<id>
 *   GET /distribution-transactions?distribution_project_ids=<id1>,<id2>,...
 * Returns: { transactions: Array<{ distribution_project_id, platform_id, platform_display_name, month_label, month_date, users, views, watch_time_seconds, revenue, revenue_status, ... }> }
 */

import { getBaseUrl } from "./baseUrl";
import { authFetch } from "./apiFetch";

/**
 * Fetch distribution transactions for one or more distribution project IDs.
 * @param {string | string[]} projectIds - Single id or array of ids (Firebase distribution doc ids).
 * @param {Object} [filters] - Optional server-side filters.
 * @param {string} [filters.months] - "6" | "12" | "all" (last N months).
 * @param {string} [filters.platformId] - Platform id to filter by (omit for all).
 * @returns {Promise<Array>} Array of transaction rows from distribution_transactions table.
 */
export async function getDistributionTransactions(projectIds, filters = {}) {
  const base = getBaseUrl();
  if (!base) return [];

  const ids = Array.isArray(projectIds) ? projectIds : [projectIds];
  if (ids.length === 0) return [];

  const params = new URLSearchParams();
  if (ids.length === 1) {
    params.set("distribution_project_id", ids[0]);
  } else {
    params.set("distribution_project_ids", ids.join(","));
  }
  if (filters.months) {
    params.set("months", filters.months);
  }
  if (filters.platformId && filters.platformId !== "all") {
    params.set("platform_id", filters.platformId);
  }

  const url = `${base}/distribution-transactions?${params.toString()}`;
  if (process.env.NODE_ENV === "development") {
    console.log("[distribution-transactions] fetching:", url);
  }
  try {
    const res = await authFetch(url);
    if (!res.ok) {
      console.error(
        "distribution-transactions API error",
        res.status,
        await res.text()
      );
      return [];
    }
    const data = await res.json();
    return data.transactions || [];
  } catch (err) {
    console.error("Failed to fetch distribution transactions", err);
    return [];
  }
}
